import { Link } from 'react-router-dom';
import { CreditCard, Clock, AlertTriangle, ChevronRight, Calendar } from 'lucide-react';
import dayjs from 'dayjs';

/**
 * SupplierPaymentList Component
 * Displays upcoming supplier payments with urgency indicators
 */
export default function SupplierPaymentList({ data, loading = false }) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-14 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { proximosPagos = [], totales = {}, moneda = 'CRC' } = data;

  // Take top 6 upcoming payments
  const payments = proximosPagos.slice(0, 6);

  // Format currency
  const formatCurrency = (value, currency) => {
    const curr = currency || moneda;
    if (curr === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Urgency styles
  const getUrgencyStyle = (urgencia) => {
    if (!urgencia) return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };

    switch (urgencia.nivel) {
      case 'vencido':
        return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', label: 'Vencido' };
      case 'urgente':
        return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', label: 'Urgente' };
      case 'proximo':
        return { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', label: 'Proximo' };
      default:
        return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', label: 'Normal' };
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
              <CreditCard className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Proximos Pagos CxP</h2>
              <p className="text-xs text-gray-500">Facturas por pagar ordenadas por vencimiento</p>
            </div>
          </div>

          {totales.totalVencido > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {formatCurrency(totales.totalVencido)} vencido
            </span>
          )}
        </div>
      </div>

      {/* Payments list */}
      {payments.length === 0 ? (
        <div className="p-8 text-center">
          <div className="h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <CreditCard className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-gray-900">Sin pagos pendientes</p>
          <p className="text-xs text-gray-500">No tienes facturas por pagar proximas</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {payments.map((payment) => {
            const urgencyStyle = getUrgencyStyle(payment.urgencia);
            const daysText = payment.diasParaVencer === 0
              ? 'Hoy'
              : payment.diasParaVencer < 0
                ? `Hace ${Math.abs(payment.diasParaVencer)}d`
                : `En ${payment.diasParaVencer}d`;

            return (
              <div
                key={payment.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Urgency indicator */}
                  <div className={`w-1.5 h-10 rounded-full ${
                    payment.urgencia?.nivel === 'vencido' ? 'bg-red-500' :
                    payment.urgencia?.nivel === 'urgente' ? 'bg-orange-500' :
                    payment.urgencia?.nivel === 'proximo' ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} />

                  {/* Payment info */}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {payment.proveedor}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{payment.consecutivo}</span>
                      <span className="text-gray-300">|</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{dayjs(payment.fechaVencimiento).format('DD/MM')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Days indicator */}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${urgencyStyle.bg} ${urgencyStyle.text}`}>
                    {daysText}
                  </span>

                  {/* Amount */}
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">
                      {formatCurrency(payment.monto, payment.moneda)}
                    </p>
                    {payment.moneda !== 'CRC' && (
                      <p className="text-xs text-gray-500">
                        {formatCurrency(payment.montoCRC)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="p-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            <span>Total pendiente: </span>
            <span className="font-medium text-gray-700">{formatCurrency(totales.totalPendiente)}</span>
          </div>
          <Link
            to="/cxp/facturas"
            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Ver todas
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
